import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';

import AddColorPopUp from '@components/colors/AddColorPopUp';
import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import AddFabric from '@components/materials/AddFabric';
import NitexImageCropper from '@components/NitexImageCropper';
import { toastError, toastSuccess, toastWarning } from '@components/Toast';

import { fetchBrandList } from '@store/actions/BrandAction';
import { updateDesignInfo } from '@store/actions/FilterAction';
import { fetchSeasonList } from '@store/actions/SeasonAction';
import {
	fetchDocResponse,
	getDesignDetailsInfo,
	getMarkets,
	getSubCategories,
	upDateDesignDetails
} from '@store/design/action';
import {
	getAllMarkets,
	getAllSuppliersMaterials,
	getDesignDetails,
	getDocumentResponse
} from '@store/reducers';
import { fetchAllSuppliersMaterials } from '@store/supplier/action';

import ApiClient from '@lib/apiClient';

import {
	isValidFile,
	renderPantoneTypeColorsHorizontally,
	renderSwatchTypeColorsHorizontally
} from '@utils/Util';

import { PANTONE_OR_HEX_CODE, VALID_FILE_SIZE } from '@/constants/constant';

const EditDesignBasicInfo = ({
	onCancelModal,
	isEditAllFields,
	productId,
	onUpdateBasicInfo,
	onUpdateDesignImage
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isCropImage, setIsCropImage] = useState(false);
	const [isUpdateDesign, setIsUpdateDesign] = useState(true);
	const [showFabricModal, setShowFabricModal] = useState(false);
	const [styleImage, setStyleImage] = useState({});
	const [designInfo, setDesignInfo] = useState({});
	const [updateFormErrors, setUpdateFormErrors] = useState({});
	const [sizeCategoryList, setSizeCategoryList] = useState([]);
	const [standardSizeList, setStandardSizeList] = useState([]);
	const [selectedSizeList, setSelectedSizeList] = useState([]);
	const [categories, setCategories] = useState([]);
	const [selectedColorList, setSelectedColorList] = useState([]);
	const [mainImageUrl, setMainImageUrl] = useState('');
	const [searchFabric, setSearchFabric] = useState('');
	const [selectedFabric, setSelectedFabric] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('');
	const [selectedBrand, setSelectedBrand] = useState('');
	const [selectedSeason, setSelectedSeason] = useState('');
	const [selectedSizeCategory, setSelectedSizeCategory] = useState('');
	const [sort] = useState('id,desc');
	const [croppedBodyData, setCroppedBodyData] = useState({
		id: '',
		base64Str: '',
		docMimeType: 'image/jpeg',
		extension: 'jpeg'
	});
	const dispatch = useDispatch();
	const design = useSelector(getDesignDetails);
	const { featureImageDocResponse } = useSelector(getDocumentResponse);
	let markets = useSelector(getAllMarkets);
	markets = markets.map((item) => {
		return {
			id: item.id,
			value: item.name
		};
	});

	let allFabrics = useSelector(getAllSuppliersMaterials);
	allFabrics = allFabrics?.data?.map((item) => {
		return {
			id: item.materialId,
			value: item.name
		};
	});
	let brandList = useSelector((store) => store.brandStore);
	brandList = brandList?.map((item) => {
		return {
			id: item.id,
			value: item.name
		};
	});

	let seasonList = useSelector((store) => store.seasonStore);
	seasonList = seasonList?.map((item) => {
		return {
			id: item.constant,
			value: item.name
		};
	});

	const getAllSizeCategory = async () => {
		await ApiClient.GET('allSizeCategory', '?productId=' + productId)
			.then(({ data }) => {
				setSizeCategoryList([
					...data.map((item) => {
						return {
							id: item.id,
							value: item.name
						};
					})
				]);
			})
			.catch(() => {
				toastError('Failed to load size categories');
			});
	};

	const getStandardSizeList = async (id) => {
		const params = `?sizeCategoryId=${id}`;
		await ApiClient.GET('getSizeList', params)
			.then(({ data }) => {
				setStandardSizeList(data);
			})
			.catch(() => {
				toastError('Failed to load size categories');
			});
	};

	const onHide = () => {
		setIsUpdateDesign(false);
		onCancelModal();
	};
	useEffect(() => {
		setIsLoading(true);
		dispatch(fetchBrandList());
		dispatch(fetchSeasonList());
		dispatch(getDesignDetailsInfo(productId)).finally(() => {
			setIsLoading(false);
		});
		dispatch(fetchDocResponse(productId)).finally(() => {
			setIsLoading(false);
		});
		dispatch(getMarkets()).finally(() => {
			setIsLoading(false);
		});
		dispatch(getSubCategories()).finally(() => {
			setIsLoading(false);
		});

		getAllSizeCategory();
	}, [dispatch, productId]);

	useEffect(() => {
		dispatch(fetchAllSuppliersMaterials(0, 20, 'FABRIC', sort, { search: searchFabric })).finally(() => {
			setIsLoading(false);
		});
	}, [searchFabric]);

	useEffect(() => {
		setIsLoading(true);
		setStyleImage(featureImageDocResponse);
		setSelectedCategory(design?.subCategoryResponse?.name);
		setSelectedFabric(design?.fabricResponse?.name);
		setStandardSizeList(design?.sizeResponseList);
		setSelectedSizeList(design?.sizeMappingList?.map((item) => item.standardSize));
		setSelectedColorList(design?.colorResponseList);
		setSelectedSizeCategory(design.sizeCategoryResponse?.name || sizeCategoryList[0]?.value);
		setSelectedSeason(design?.seasonResponse?.name);
		setSelectedBrand(design?.brandResponse?.name);
		setDesignInfo({
			...designInfo,
			id: design.id,
			name: design?.name,
			brandId: design?.brandResponse?.id,
			season: design?.seasonResponse?.code,
			referenceNumber: design?.referenceNumber,
			productGroupId: design?.marketResponse?.id,
			productSubCategoryId: design?.subCategoryResponse?.id,
			fabricId: design?.fabricResponse?.id,
			sizeCategoryId: design.sizeCategoryResponse?.id || sizeCategoryList[0]?.id
		});
		setIsLoading(false);
	}, [featureImageDocResponse, design]);

	useEffect(() => {
		fetchSubCategories(design?.marketResponse?.id);
	}, [design]);

	const fetchSubCategories = (productGroupId) => {
		ApiClient.GET('fetchSubCategory', `${productGroupId ? '?productGroupIds=' + productGroupId : ''}`)
			.then((response) => {
				let tmpCategories = [];
				for (let cat of response.data) {
					for (let item of cat.subCategoryResponseList) {
						tmpCategories.push({ id: item.id, value: item.name, isPrimary: item.isPrimary });
					}
				}
				setCategories(tmpCategories);
				setIsLoading(false);
			})
			.catch((error) => {
				setIsLoading(false);
				toastError(error.response.data.message);
			});
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		if (name === 'market') {
			fetchSubCategories(value);
			setUpdateFormErrors({ ...updateFormErrors, marketError: '' });
			setDesignInfo({
				...designInfo,
				productGroupId: value
			});
		} else {
			setDesignInfo({
				...designInfo,
				[name]: value
			});
		}
	};

	const onItemClick = (item) => {
		if (item?.title === 'Category') {
			setSelectedCategory(item.value);
			setDesignInfo({ ...designInfo, productSubCategoryId: item.id });
			setUpdateFormErrors({ ...updateFormErrors, categoryError: '' });
		}
		if (item?.title === 'Brand') {
			setSelectedBrand(item.value);
			setDesignInfo({ ...designInfo, brandId: item.id });
		}
		if (item?.title === 'Season') {
			setSelectedSeason(item.value);
			setDesignInfo({ ...designInfo, season: item.id });
		}
		if (item?.title === 'Fabric') {
			setSelectedFabric(item.value);
			setDesignInfo({ ...designInfo, fabricId: item.id });
			setUpdateFormErrors({ ...updateFormErrors, fabricError: '' });
		}
		if (item?.title === 'Size Category') {
			setSelectedSizeCategory(item.value);
			setDesignInfo({ ...designInfo, sizeCategoryId: item.id });
			getStandardSizeList(item.id);
		}
	};

	const onSizeClick = (item) => {
		if (isSelectedColor(item.code)) {
			const sizes = selectedSizeList?.filter((size) => size !== item.code);
			setSelectedSizeList([...sizes]);
		} else {
			setSelectedSizeList([...selectedSizeList, item.code]);
		}
	};
	const isSelectedColor = (code) => selectedSizeList.includes(code);

	const isValidInputForm = () => {
		if (!designInfo?.productGroupId) {
			setUpdateFormErrors({ ...updateFormErrors, marketError: 'Market is required' });
			return false;
		}
		if (!designInfo?.productSubCategoryId) {
			setUpdateFormErrors({ ...updateFormErrors, categoryError: 'Category is required' });
			return false;
		}
		if (!designInfo?.fabricId) {
			setUpdateFormErrors({ ...updateFormErrors, fabricError: 'Fabric is required' });
			return false;
		}
		return true;
	};

	const onUpdate = async () => {
		const isValid = isValidInputForm();
		if (!isValid) return;
		const body = {
			id: designInfo.id,
			name: designInfo?.name,
			referenceNumber: designInfo?.referenceNumber,
			productSubCategoryId: designInfo?.productSubCategoryId,
			brandId: designInfo?.brandId,
			season: designInfo?.season,
			productGroupId: designInfo?.productGroupId,
			fabricId: designInfo?.fabricId,
			sizeList: selectedSizeList?.map((item) => item),
			sizeCategoryId: designInfo?.sizeCategoryId
		};
		setIsLoading(true);
		await ApiClient.PUT('upDateDesign', body, design?.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					onCancelModal();
					toastSuccess(data.message);
					if (onUpdateBasicInfo) {
						onUpdateBasicInfo(data.payload);
					}
					dispatch(updateDesignInfo({ ...body, fabricDetails: selectedFabric })).then(() =>
						setIsLoading(false)
					);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const onDeleteImage = async (docId) => {
		await ApiClient.DELETE('deleteProductDocument', {}, `${productId}/${docId}`)
			.then(() => {
				dispatch(fetchDocResponse(productId)).finally(() => {
					setIsLoading(false);
				});
			})
			.catch(({ response }) => {
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onAddProductImage = async (data) => {
		setIsLoading(true);
		await ApiClient.POST('uploadDocumentInProduct', data, productId)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					if (styleImage?.id) {
						onDeleteImage(styleImage.id);
					}
					setStyleImage(data.payload);
					setDesignInfo({ ...designInfo, documentId: data.id });
					if (onUpdateDesignImage) {
						onUpdateDesignImage(productId, data?.payload?.docUrl);
					}
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUpload = (e, docType = 'PRODUCT_DESIGN') => {
		let file = e.target.files[0];
		if (file) {
			if (isValidFile(file, file?.type, VALID_FILE_SIZE)) {
				let data = {
					name: file?.name,
					docMimeType: file?.type,
					documentType: docType,
					base64Str: ''
				};
				let reader = new FileReader();
				reader.readAsDataURL(file);
				reader.onload = () => {
					data.base64Str = reader.result;
					onAddProductImage(data);
				};
				reader.onerror = function (error) {
					toastError(error);
				};
			} else {
				toastWarning('Please upload file less than 12mb');
			}
		}
	};

	const onFabricSearch = (value) => {
		setSearchFabric(value);
	};

	const onCroppedDataSave = (base64str) => {
		if (base64str) {
			setIsLoading(true);
			dispatch(upDateDesignDetails({ ...croppedBodyData, base64Str: base64str }, productId)).finally(
				() => {
					setIsLoading(false);
				}
			);
		} else {
			toastError('Crop was not successful');
		}
		onClose();
	};

	const onEdit = (e, id, img) => {
		setIsCropImage(true);
		setCroppedBodyData({ ...croppedBodyData, id: id });
		setMainImageUrl(img);
	};

	const onClose = () => {
		setIsCropImage(false);
		setMainImageUrl('');
	};

	const onRemoveColor = async (itemId) => {
		setIsLoading(true);
		await ApiClient.POST('removeColor', [itemId], designInfo?.id)
			.then(({ data }) => {
				setIsLoading(false);
				if (data?.message) {
					const newList = selectedColorList.filter((item) => item.id !== itemId);
					setSelectedColorList(newList);
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onAddNewColors = (item) => {
		setSelectedColorList([...selectedColorList, item]);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<Modal
				show={isUpdateDesign}
				onHide={onHide}
				className="auto-width-popup design-basic-info-popup"
				backdrop={false}
			>
				<Modal.Body>
					<div className="design-basic-info-edit-modal shadow-24dp">
						<div className="design-info-container d-flex">
							<LoaderComponent loading={isLoading}>
								<div className="left-half">
									<div className="image-box">
										<img src={styleImage?.docUrl} alt="image" />

										<div className="edit-buttons">
											<img
												src={`${process.env.PUBLIC_URL}/icons/crop.svg`}
												alt="crop"
												onClick={(e) => onEdit(e, styleImage?.id, styleImage?.docUrl)}
											/>

											<label htmlFor="design-upload" className="drag-upload">
												<img
													src={`${process.env.PUBLIC_URL}/icons/upload-gray.svg`}
													alt="upload"
												/>
											</label>
											<input
												type="file"
												className="file-upload d-none"
												id="design-upload"
												onChange={onUpload}
												accept="image/*"
											/>
										</div>
									</div>
								</div>
							</LoaderComponent>
							<div className="right-half">
								<div className="design-update-table">
									<table className="design-basic-info-table">
										<tbody>
											{isEditAllFields && (
												<>
													<tr>
														<td colSpan={10}>
															<input
																type="text"
																autoComplete="off"
																placeholder="Design name"
																maxLength={48}
																className="border-0"
																name="name"
																value={designInfo.name}
																onChange={handleChange}
															/>
														</td>
													</tr>
													<tr>
														<td colSpan={10}>
															<input
																type="text"
																autoComplete="off"
																placeholder="Design number"
																maxLength={32}
																className="border-0"
																name="referenceNumber"
																value={designInfo.referenceNumber}
																onChange={handleChange}
															/>
														</td>
													</tr>
												</>
											)}
											<tr>
												<td colSpan={5} className="market">
													<div className="form-group">
														<select
															name="market"
															id="market"
															onChange={handleChange}
															value={designInfo?.productGroupId || ''}
														>
															<option value="">Market</option>
															{markets.map((item) => (
																<option value={item.id} key={item.id}>
																	{item.value}
																</option>
															))}
														</select>
														{updateFormErrors.marketError && (
															<p className="error">
																{updateFormErrors.marketError}
															</p>
														)}
													</div>
												</td>
												<td colSpan={5} className="category">
													<div className="category-select">
														<CustomDropdown
															type="addItem"
															title={'Category'}
															toolTipLength={18}
															onItemClick={onItemClick}
															items={categories}
															selectedItem={selectedCategory}
														/>
													</div>
													{updateFormErrors.categoryError && (
														<p className="error">
															{updateFormErrors.categoryError}
														</p>
													)}
												</td>
											</tr>
											{design?.extraFlag?.isSupplierDeveloped === true && (
												<tr>
													<td colSpan={5} className="category">
														<div className="category-select">
															<CustomDropdown
																type="addItem"
																title={'Brand'}
																toolTipLength={18}
																onItemClick={onItemClick}
																items={brandList}
																selectedItem={selectedBrand}
															/>
														</div>
													</td>
													<td colSpan={5} className="category">
														<div className="category-select">
															<CustomDropdown
																type="addItem"
																title={'Season'}
																toolTipLength={18}
																onItemClick={onItemClick}
																items={seasonList}
																selectedItem={selectedSeason}
															/>
														</div>
													</td>
												</tr>
											)}
											<tr>
												<td colSpan={10}>
													<div className="fabric-select">
														<CustomDropdown
															type="addItem"
															title={'Fabric'}
															isAddNew={true}
															items={allFabrics}
															selectedItem={selectedFabric}
															onItemSearch={onFabricSearch}
															onItemClick={(value) => {
																if (value.isAddNew) {
																	setShowFabricModal(true);
																} else {
																	onItemClick(value);
																}
															}}
														/>
													</div>
													{updateFormErrors.fabricError && (
														<p className="error">
															{updateFormErrors.fabricError}
														</p>
													)}
													{showFabricModal && (
														<AddFabric
															isShow={true}
															onCloseModal={() => setShowFabricModal(false)}
															onItemClick={onItemClick}
														/>
													)}
												</td>
											</tr>
											<tr className="sizes-row">
												<td colSpan={4}>
													<div className="fabric-select">
														<CustomDropdown
															type="addItem"
															title={'Size Category'}
															toolTipLength={13}
															onItemClick={onItemClick}
															items={sizeCategoryList}
															selectedItem={selectedSizeCategory}
														/>
													</div>
												</td>
												{standardSizeList?.slice(0, 6).map((item) => (
													<td
														className={`size ${
															isSelectedColor(item.code) ? 'selected' : ''
														}`}
														key={item.code}
														onClick={() => onSizeClick(item)}
													>
														<span>{item.value}</span>
													</td>
												))}
											</tr>
											<tr className="sizes-row">
												{standardSizeList
													?.slice(6, standardSizeList.length)
													.map((item) => (
														<td
															className={`size ${
																isSelectedColor(item.code) ? 'selected' : ''
															}`}
															key={item.code}
															onClick={() => onSizeClick(item)}
														>
															<span>{item.value}</span>
														</td>
													))}
											</tr>
											<tr className="colors-row">
												<td colSpan={10}>
													<div className="col-md-12">
														<div className="colors-row">
															<div className="d-flex align-items-center">
																<AddColorPopUp
																	selectedColors={selectedColorList}
																	onAddNewColors={onAddNewColors}
																	productId={designInfo?.id}
																/>

																{selectedColorList?.map((value) =>
																	value?.representedBy ===
																	PANTONE_OR_HEX_CODE
																		? renderPantoneTypeColorsHorizontally(
																				value,
																				onRemoveColor
																			)
																		: renderSwatchTypeColorsHorizontally(
																				value,
																				onRemoveColor
																			)
																)}
															</div>
														</div>
													</div>
													{isCropImage && (
														<NitexImageCropper
															isEdit={isCropImage}
															onClose={onClose}
															imageSrc={mainImageUrl}
															onSave={onCroppedDataSave}
														/>
													)}
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
						<div className="footer-buttons text-right mt-2 pt-2">
							<span className="regular-14 gray_dark_02 cursor-pointer" onClick={onCancelModal}>
								CANCEL
							</span>
							<span
								className="regular-14 primary_bright_01 ml-3 cursor-pointer"
								onClick={onUpdate}
							>
								UPDATE
							</span>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</LoaderComponent>
	);
};

export default EditDesignBasicInfo;
